import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  App,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  type TableColumnsType,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { Can } from '../../rbac/RequirePermission';
import { usePermissions } from '../../auth/store';
import { errMsg } from '../../lib/errors';
import { paiseToRupees, rupeesToPaise } from '../../lib/money';
import { adminLinks } from '../../lib/adminLinks';
import { QueryError } from '../../lib/QueryState';
import { formatBps, percentToBps, type AgencyDetail, type AgencyListRow, type AgencySettings } from './agencyApi';
import { Amounts } from './agencyViews';

/** Agencies: list, create, and the global commission & payout policy. */
export function AgenciesPage() {
  const [status, setStatus] = useState<string | undefined>();
  const [q, setQ] = useState('');
  const [creating, setCreating] = useState(false);
  const query = useQuery({
    queryKey: ['admin', 'agencies', status, q],
    queryFn: async () => (await api.get<AgencyListRow[]>('/admin/agencies', { params: { status, q: q || undefined } })).data,
  });

  const columns: TableColumnsType<AgencyListRow> = [
    {
      title: 'Agency',
      key: 'name',
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Link to={adminLinks.agency(r.id)}>{r.name}</Link>
          <Typography.Text type="secondary" copyable={{ text: r.code }} style={{ fontSize: 12 }}>
            {r.code}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (v: string) => <Tag color={v === 'active' ? 'green' : 'red'}>{v === 'active' ? 'Active' : 'Suspended'}</Tag>,
    },
    {
      title: 'Commission',
      key: 'rate',
      render: (_, r) =>
        !r.commissionEnabled ? (
          <Tag>Off</Tag>
        ) : (
          <span>
            {formatBps(r.effectiveCommissionBps)}
            {r.commissionBps === null && <Typography.Text type="secondary"> (default)</Typography.Text>}
          </span>
        ),
    },
    { title: 'Users', dataIndex: 'users', render: (v: number) => v.toLocaleString('en-IN') },
    { title: 'Commission earned', key: 'earned', render: (_, r) => <Amounts rows={r.commission.accrued} /> },
    {
      title: 'Portal login',
      key: 'login',
      render: (_, r) =>
        r.login ? (
          <span>
            {r.login.email} {r.login.disabled && <Tag color="red">Disabled</Tag>}
          </span>
        ) : (
          <Typography.Text type="secondary">None</Typography.Text>
        ),
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
        <Typography.Title level={3} style={{ margin: 0 }}>
          Agencies
        </Typography.Title>
        <Can permission="agencies.create">
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreating(true)}>
            New agency
          </Button>
        </Can>
      </Space>

      <AgencySettingsCard />

      <Card>
        <Space wrap style={{ marginBottom: 12 }}>
          <Input.Search placeholder="Name or code" allowClear onSearch={setQ} style={{ width: 240 }} />
          <Select
            allowClear
            placeholder="Any status"
            style={{ width: 160 }}
            value={status}
            onChange={setStatus}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'suspended', label: 'Suspended' },
            ]}
          />
        </Space>
        {query.isError ? (
          <QueryError error={query.error} what="agencies" onRetry={() => void query.refetch()} />
        ) : (
          <Table rowKey="id" size="small" loading={query.isLoading} columns={columns} dataSource={query.data ?? []} scroll={{ x: 900 }} />
        )}
      </Card>

      <CreateAgencyModal open={creating} onClose={() => setCreating(false)} />
    </Space>
  );
}

interface SettingsForm {
  defaultPct: number;
  commissionBase: AgencySettings['commissionBase'];
  commissionWindowDays: number;
  holdDays: number;
  minPayoutRupees: number;
  tdsPct: number;
  portalPayoutRequests: boolean;
  attachWindowDays: number;
}

interface CreateForm {
  name: string;
  code: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  ratePct?: number | null;
  notes?: string;
}

function AgencySettingsCard() {
  const qc = useQueryClient();
  const { message } = App.useApp();
  const { can } = usePermissions();
  const editable = can('agencies.settings.edit');
  const [form] = Form.useForm<SettingsForm>();
  const query = useQuery({
    queryKey: ['admin', 'agency-settings'],
    queryFn: async () => (await api.get<AgencySettings>('/admin/agency-settings')).data,
  });
  const save = useMutation({
    mutationFn: (v: SettingsForm) =>
      api.put('/admin/agency-settings', {
        defaultCommissionBps: percentToBps(v.defaultPct),
        commissionBase: v.commissionBase,
        commissionWindowDays: v.commissionWindowDays,
        holdDays: v.holdDays,
        minPayoutPaise: String(rupeesToPaise(v.minPayoutRupees)),
        tdsBps: percentToBps(v.tdsPct),
        portalPayoutRequests: v.portalPayoutRequests,
        attachWindowDays: v.attachWindowDays,
      }),
    onSuccess: () => {
      message.success('Agency settings saved — they apply to new recharges and payouts');
      void qc.invalidateQueries({ queryKey: ['admin', 'agency-settings'] });
      void qc.invalidateQueries({ queryKey: ['admin', 'agencies'] });
    },
    onError: (e) => message.error(errMsg(e, 'Could not save settings')),
  });
  const s = query.data;
  return (
    <Card title="Commission & payout settings" size="small">
      {query.isError && <QueryError error={query.error} what="agency settings" onRetry={() => void query.refetch()} />}
      {s && (
          <Form<SettingsForm>
            form={form}
            layout="vertical"
            disabled={!editable}
            initialValues={{
              defaultPct: s.defaultCommissionBps / 100,
              commissionBase: s.commissionBase,
              commissionWindowDays: s.commissionWindowDays,
              holdDays: s.holdDays,
              minPayoutRupees: paiseToRupees(s.minPayoutPaise),
              tdsPct: s.tdsBps / 100,
              portalPayoutRequests: s.portalPayoutRequests,
              attachWindowDays: s.attachWindowDays,
            }}
            onFinish={(v) => save.mutate(v)}
          >
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Changes apply to recharges and payouts from now on. Commission already earned keeps the rate it was earned at."
            />
            <Row gutter={16}>
              <Col xs={24} md={6}>
                <Form.Item
                  name="defaultPct"
                  label="Default commission %"
                  tooltip="Used for every agency without its own rate. 0 = no commission."
                  rules={[{ required: true }]}
                >
                  <InputNumber min={0} max={50} step={0.5} addonAfter="%" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item name="commissionBase" label="Commission on" rules={[{ required: true }]}>
                  <Select
                    options={[
                      { value: 'net_of_tax', label: 'Recharge minus GST' },
                      { value: 'gross', label: 'Full recharge amount' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item
                  name="commissionWindowDays"
                  label="Commission window (days)"
                  tooltip="Recharges within this many days of the user joining the agency earn commission. 0 = for life."
                  rules={[{ required: true }]}
                >
                  <InputNumber min={0} max={3650} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item
                  name="holdDays"
                  label="Hold period (days)"
                  tooltip="Commission waits this long before it can be paid out, in case the recharge is refunded."
                  rules={[{ required: true }]}
                >
                  <InputNumber min={0} max={90} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item name="minPayoutRupees" label="Minimum payout" rules={[{ required: true }]}>
                  <InputNumber min={0} max={1_000_000} addonBefore="₹" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item
                  name="tdsPct"
                  label="TDS on payouts %"
                  tooltip="Withheld from each payout. 0 = none."
                  rules={[{ required: true }]}
                >
                  <InputNumber min={0} max={30} step={0.5} addonAfter="%" style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item
                  name="attachWindowDays"
                  label="Code entry window (days)"
                  tooltip="How many days after sign-up a user can still enter an agency code."
                  rules={[{ required: true }]}
                >
                  <InputNumber min={0} max={365} style={{ width: '100%' }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={6}>
                <Form.Item
                  name="portalPayoutRequests"
                  label="Agencies can request payouts"
                  valuePropName="checked"
                  tooltip="Off = only finance creates agency payouts."
                >
                  <Switch />
                </Form.Item>
              </Col>
            </Row>
            {editable && (
              <Button type="primary" htmlType="submit" loading={save.isPending}>
                Save settings
              </Button>
            )}
          </Form>
      )}
    </Card>
  );
}

function CreateAgencyModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [form] = Form.useForm<CreateForm>();
  const create = useMutation({
    mutationFn: async (v: CreateForm) =>
      (
        await api.post<AgencyDetail>('/admin/agencies', {
          name: v.name,
          code: v.code,
          contactName: v.contactName || null,
          contactPhone: v.contactPhone || null,
          contactEmail: v.contactEmail || null,
          commissionBps: v.ratePct === null || v.ratePct === undefined ? null : percentToBps(v.ratePct),
          notes: v.notes || null,
        })
      ).data,
    onSuccess: (a) => {
      message.success(`Agency ${a.code} created`);
      void qc.invalidateQueries({ queryKey: ['admin', 'agencies'] });
      form.resetFields();
      onClose();
      navigate(adminLinks.agency(a.id));
    },
    onError: (e) => message.error(errMsg(e, 'Could not create the agency')),
  });
  return (
    <Modal
      open={open}
      title="New agency"
      onCancel={onClose}
      okText="Create"
      confirmLoading={create.isPending}
      onOk={() => form.submit()}
      destroyOnClose
    >
      <Form<CreateForm> form={form} layout="vertical" onFinish={(v) => create.mutate(v)}>
        <Form.Item name="name" label="Agency name" rules={[{ required: true, min: 2 }]}>
          <Input />
        </Form.Item>
        <Form.Item
          name="code"
          label="Agency code"
          extra="8–12 letters or digits (dashes are ignored), e.g. AG-CHN-0001. Users type it in the app's referral-code box, or share dudesocial.in/r/<code>."
          rules={[
            { required: true },
            {
              validator: (_, v: string | undefined) => {
                const n = (v ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');
                return n.length >= 8 && n.length <= 12
                  ? Promise.resolve()
                  : Promise.reject(new Error('8–12 letters or digits'));
              },
            },
          ]}
        >
          <Input style={{ textTransform: 'uppercase' }} />
        </Form.Item>
        <Form.Item name="contactName" label="Contact person">
          <Input />
        </Form.Item>
        <Form.Item name="contactPhone" label="Contact phone">
          <Input />
        </Form.Item>
        <Form.Item name="contactEmail" label="Contact email" rules={[{ type: 'email' }]}>
          <Input />
        </Form.Item>
        <Can permission="agencies.commission.edit">
          <Form.Item name="ratePct" label="Commission % (leave empty to use the default)">
            <InputNumber min={0} max={50} step={0.5} addonAfter="%" style={{ width: '100%' }} />
          </Form.Item>
        </Can>
        <Form.Item name="notes" label="Notes">
          <Input.TextArea rows={2} />
        </Form.Item>
      </Form>
      <Typography.Text type="secondary">Add payout details and a portal login on the next page.</Typography.Text>
    </Modal>
  );
}
